// src/theme.js
const appTheme = {
  colors: {
    // PRIMARY: Transform green and red into luxury tones
    primary: '#1a4d3a',          // Deep forest emerald (NOT typical restaurant green)
    secondary: '#8b1538',        // Rich burgundy wine (NOT bright red)
    accent: '#d4af37',           // Pure gold (luxury differentiation)
    
    // SIGNATURE COLORS: Your unique identity
    champagne: '#f7e7ce',        // Sophisticated cream base
    charcoal: '#2d3436',         // Premium dark alternative  
    platinum: '#e8eaed',         // Modern silver undertone
    
    // SOPHISTICATED PASTELS: Elevated from #fcffe9
    ivoryMist: '#fcffe9',        // Your reference - now "ivory mist"
    jadeFog: '#e6f2e6',          // Misty green (completely different feel)
    rosePetal: '#f4e8e8',        // Soft blush (not pink)
    goldenHour: '#faf6e8',       // Warm luxury backdrop
    
    // PREMIUM SUPPORTING PALETTE
    deepTeal: '#0f4c42',         // Sophisticated depth
    warmCopper: '#b7472a',       // Rich earth tone
    coolGray: '#5d6d7e',         // Modern neutral
    warmGray: '#8b7d73',         // Sophisticated beige
    
    // FUNCTIONAL COLORS: Restaurant optimized
    success: '#2ecc71',          // Fresh green for confirmations
    warning: '#f39c12',          // Amber for attention
    error: '#c0392b',            // Clean error red
    info: '#3498db',             // Trust-building blue
    
    // TEXT HIERARCHY: Luxury feel
    textPrimary: '#2d3436',      // Rich charcoal
    textSecondary: '#5d6d7e',    // Cool sophisticated gray  
    textMuted: '#8b7d73',        // Warm muted
    textLight: '#b2b8bd',        // Light elegant
    textGold: '#d4af37',         // Gold highlights
    
    // SURFACES: Premium materials feel
    surface: '#ffffff',
    surfaceLuxury: '#fcffe9',    // Your signature background
    surfaceElevated: '#f7e7ce',  // Champagne cards
    surfaceDepth: '#e8eaed',     // Platinum depth
    
    // BORDERS & DIVIDERS
    borderPrimary: '#e8eaed',
    borderAccent: '#d4af37',
    borderSubtle: '#f4f6f7'
  },
  
  spacing: {
    micro: '2px',
    tiny: '4px', 
    xs: '8px',
    sm: '12px',
    base: '16px',
    md: '20px',
    lg: '24px',
    xl: '32px',
    xxl: '40px',
    huge: '64px'
  },
  
  borderRadius: {
    sharp: '0px',
    subtle: '3px',
    soft: '6px', 
    medium: '10px',
    rounded: '16px',
    large: '24px',
    pill: '50px',
    circle: '50%'
  },
  
  // LUXURY SHADOW SYSTEM
  shadows: {
    // Sophisticated depth using primary colors
    whisper: '0 1px 3px rgba(26, 77, 58, 0.04)',
    soft: '0 2px 6px rgba(26, 77, 58, 0.06)',
    medium: '0 4px 12px rgba(26, 77, 58, 0.08)',
    strong: '0 8px 20px rgba(26, 77, 58, 0.10)',
    dramatic: '0 16px 40px rgba(26, 77, 58, 0.12)',
    
    // Premium colored shadows
    goldGlow: '0 4px 20px rgba(212, 175, 55, 0.15)',
    burgundyDepth: '0 6px 16px rgba(139, 21, 56, 0.12)',
    emeraldMist: '0 3px 12px rgba(26, 77, 58, 0.08)'
  },
  
  // LUXURY GRADIENT SYSTEM  
  gradients: {
    // Signature combinations
    hero: 'linear-gradient(135deg, #1a4d3a 0%, #8b1538 100%)',
    luxury: 'linear-gradient(135deg, #f7e7ce 0%, #fcffe9 50%, #faf6e8 100%)',
    premium: 'linear-gradient(145deg, #2d3436 0%, #1a4d3a 100%)',
    golden: 'linear-gradient(135deg, #d4af37 0%, #f7e7ce 100%)',
    
    // Surface gradients
    cardLuxury: 'linear-gradient(145deg, #ffffff 0%, #fcffe9 100%)',
    cardPremium: 'linear-gradient(145deg, #f7e7ce 0%, #faf6e8 100%)',
    
    // Special effects
    shimmer: 'linear-gradient(90deg, transparent 0%, rgba(212, 175, 55, 0.4) 50%, transparent 100%)'
  },
  
  // TYPOGRAPHY: Luxury restaurant feel
  typography: {
    fontSizes: {
      micro: '10px',
      tiny: '12px', 
      small: '14px',
      base: '16px',
      medium: '18px',
      large: '20px',
      xl: '24px',
      xxl: '30px',
      hero: '36px',
      giant: '48px'
    },
    
    lineHeights: {
      tight: '1.2',
      snug: '1.3', 
      normal: '1.5',
      relaxed: '1.6',
      loose: '1.8'
    },
    
    fontWeights: {
      thin: '200',
      light: '300',
      regular: '400', 
      medium: '500',
      semibold: '600',
      bold: '700',
      black: '900'
    }
  },
  
  // PREMIUM ANIMATIONS
  transitions: {
    instant: '100ms ease-out',
    fast: '200ms ease-out', 
    smooth: '300ms ease-out',
    slow: '500ms ease-out',
    luxury: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },
  
  // RESTAURANT-SPECIFIC FEATURES
  restaurant: {
    // Menu category colors
    appetizer: '#e6f2e6',
    mainCourse: '#f7e7ce', 
    dessert: '#f4e8e8',
    beverage: '#faf6e8',
    
    // Order status colors
    pending: '#f39c12',
    preparing: '#3498db',
    ready: '#2ecc71', 
    delivered: '#d4af37',
    
    // Rating colors
    excellent: '#d4af37',
    good: '#2ecc71',
    average: '#f39c12',
    poor: '#c0392b'
  }
};

// ==========================================
// CHEF THEME (Crispy Dosa Visual Identity)
// ==========================================
export const chefTheme = {
  colors: {
    // PRIMARY: Fresh greens and whites
    primary: '#2E7D32',          // Fresh vibrant green
    secondary: '#4CAF50',        // Lighter green accent
    accent: '#FF8F00',           // Warm amber for CTAs
    
    // SIGNATURE COLORS
    champagne: '#E8F5E9',        // Mint tint (replacing champagne)
    charcoal: '#1B2631',         // Clean dark text
    platinum: '#F5F5F5',         // Soft gray
    
    // PASTELS
    ivoryMist: '#FAFFFE',        // Very subtle mint/white background
    jadeFog: '#C8E6C9',          // Soft sage green
    rosePetal: '#FBE9E7',        // Soft subtle warmth
    goldenHour: '#FFF8E1',       // Lemon tint
    
    // PREMIUM SUPPORTING
    deepTeal: '#1B5E20',         // Dark forest green
    warmCopper: '#E65100',       // Deep amber/orange
    coolGray: '#5D6D7E',
    warmGray: '#90A4AE',         // Clean blue-gray
    
    // FUNCTIONAL
    success: '#388E3C',
    warning: '#F57C00',
    error: '#D32F2F',
    info: '#1976D2',
    
    // TEXT HIERARCHY
    textPrimary: '#1B2631',
    textSecondary: '#5D6D7E',
    textMuted: '#90A4AE',
    textLight: '#B0BEC5',
    textGold: '#FF8F00',
    
    // SURFACES
    surface: '#FFFFFF',
    surfaceLuxury: '#F4F9F4',    // Clean minty-white background
    surfaceElevated: '#FFFFFF',  // Pure white cards
    surfaceDepth: '#E8F5E9',     // Mint depth
    
    // BORDERS
    borderPrimary: '#C8E6C9',    // Sage border
    borderAccent: '#4CAF50',
    borderSubtle: '#F1F8F1'
  },
  
  spacing: appTheme.spacing,      // Share spacing
  borderRadius: appTheme.borderRadius, // Share border radius
  
  // FLATTER, CLEANER SHADOWS
  shadows: {
    whisper: '0 1px 2px rgba(46, 125, 50, 0.05)',
    soft: '0 2px 4px rgba(46, 125, 50, 0.08)',
    medium: '0 4px 8px rgba(46, 125, 50, 0.1)',
    strong: '0 8px 16px rgba(46, 125, 50, 0.12)',
    dramatic: '0 12px 24px rgba(46, 125, 50, 0.15)',
    
    goldGlow: '0 4px 12px rgba(255, 143, 0, 0.2)',
    burgundyDepth: '0 4px 12px rgba(211, 47, 47, 0.15)',
    emeraldMist: '0 4px 12px rgba(46, 125, 50, 0.15)'
  },
  
  // CLEANER GRADIENTS
  gradients: {
    hero: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
    luxury: 'linear-gradient(135deg, #FFFFFF 0%, #F4F9F4 100%)',
    premium: 'linear-gradient(145deg, #2E7D32 0%, #4CAF50 100%)',
    golden: 'linear-gradient(135deg, #FF8F00 0%, #FFB300 100%)',
    
    cardLuxury: 'linear-gradient(145deg, #FFFFFF 0%, #FAFFFE 100%)',
    cardPremium: 'linear-gradient(145deg, #F4F9F4 0%, #E8F5E9 100%)',
    shimmer: 'linear-gradient(90deg, transparent 0%, rgba(46, 125, 50, 0.1) 50%, transparent 100%)'
  },
  
  typography: appTheme.typography,
  transitions: appTheme.transitions,
  
  restaurant: {
    appetizer: '#E8F5E9',
    mainCourse: '#FFF8E1', 
    dessert: '#FBE9E7',
    beverage: '#E3F2FD',
    
    pending: '#F57C00',
    preparing: '#1976D2',
    ready: '#388E3C', 
    delivered: '#FF8F00',
    
    excellent: '#388E3C',
    good: '#4CAF50',
    average: '#FF9800',
    poor: '#D32F2F'
  }
};

export default appTheme;
